import React, { useEffect, useRef, useState } from 'react';
import { useAolStore } from '../../../context/AolContext';
import useAolSaveHandler from '../../../app/useAolSaveHandler';
import { useVesselStore } from '../../../context/VesselContext';
import { FuelRobDataTypeEnum, FuelTypeEnum, NoonReportSectionEnum } from '../../../types/ViewModel/AolEnums';
import { AolDataItem } from '../../../types/ViewModel/AolItem';
import { FuelRobPerTank } from '../../../types/ViewModel/FuelRobPerTank';
import { createSectionData, GetFuelType } from '../../../utils/aolFieldsHelper';
import FuelRobPerTypeComponent from './fuel-rob-per-type/FuelRobPerType';
import { NoonReportFuelRobSectionFactory } from '../../../factory/ValidatedNoonReportFactory';
import { NoonReportSectionData } from '../../../types/ViewModel/NoonReportSectionData';
import { FuelRobData } from '../../../types/ViewModel/FuelRobData';
import FuelRobBunkerComponent from './fuel-rob-per-type/FuelRobBunker';
import { Wrapper } from './FuelRobSection.style';
import { ErrorText } from '../../shared/error-text/ErrorText.style';
import { truncateTo2Decimals } from '../../../utils/numberHelper';
import { hasBunkering } from '../../../utils/aolSectionHelper';
import usePostUpdateAolSection from '../../../api/usePostUpdateAolSection';
import { prettifyDateTimeHHmm } from 'odfjell-date-time-utils/dateTimeFormatter';
import { isDateBefore, isDateSame } from 'odfjell-date-time-utils/dateTimeUtils';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';
import { isNullOrUndefined } from 'src/utils/objectUtils';

type FuelRobVM = {
    isFirstRobSectionInAol?: boolean;
    portName: string;
    fuelRobHfoPerTankViewModels?: FuelRobPerTank[];
    fuelRobMgoPerTankViewModels?: FuelRobPerTank[];
    dateUtc: Date;
    eventDateTime?: Date;
    fuelRobDataType: FuelRobDataTypeEnum;
    indexInDay: number;
    hasSyncError?: boolean;
};

type VM = {
    fuelRobData?: FuelRobData[];
    sectionData?: NoonReportSectionData;
    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;
    isInitialized?: boolean;
};

const FuelRobSectionComponent: React.FC = () => {
    const [vm, setVM] = useState<VM>({});
    const [syncedFuelRobDatas, setSyncedFuelRobDatas] = useState<FuelRobData[]>([]);
    const { vessel } = useVesselStore();
    const { selectedAolDataItem, selectedAolGroup, aolGroups, selectedUnit, previousAolGroup, hasSyncError, setHasSyncError } = useAolStore();
    const [fuelRobVMs, setFuelRobVMs] = useState<FuelRobVM[]>([]);
    const { syncFuelRobMismatch } = usePostUpdateAolSection();
    const { saveAolEvent, saveSection } = useAolSaveHandler();
    const hasEmptyHfoValues = useRef(false);
    const hasEmptyMgoValues = useRef(false);
    const [hasNewBunkeringSyncError, setHasNewBunkeringSyncError] = useState<boolean>(false);
    const [hasRemovedBunkeringSyncError, setHasRemovedBunkeringSyncError] = useState<boolean>(false);
    const [hasChangedBunkeringTimeSyncError, setHasChangedBunkeringTimeSyncError] = useState<boolean>(false);
    const [changedBunkeringTimeSyncErrorMessage, setChangedBunkeringTimeSyncErrorMessage] = useState<string>('');
    const { checkIsAolReadOnly } = useReadOnlyUtils();

    useEffect(() => {
        if (!aolGroups || !selectedAolDataItem || !vessel || !selectedAolGroup) return;

        if (!vm.isInitialized || vm.sectionData !== selectedAolDataItem.noonReport?.fuelRobSection?.sectionData) {
            initVM();
        }
    }, [aolGroups, selectedAolDataItem, vessel, selectedAolGroup, previousAolGroup]);

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.FuelRob &&
            selectedAolDataItem &&
            saveAolEvent.aolDataItem.id === selectedAolDataItem.id
        ) {
            if (saveAolEvent.type === 'save_section' && !hasEmptyHfoValues.current && !hasEmptyMgoValues.current) save();
            if (saveAolEvent.type === 'save_section_done') initVM();
        }
    }, [saveAolEvent]);

    const isReadOnly = checkIsAolReadOnly(vm?.sectionData);

    const updatePreviousROB = (fuelRobVm: FuelRobVM, fuelRobPerTank: FuelRobPerTank) => {
        if (fuelRobVm.fuelRobDataType === FuelRobDataTypeEnum.MeasureAfterBunkering) {
            const vms = [...fuelRobVMs];
            if (fuelRobPerTank.fuelType === FuelTypeEnum.HFO) {
                vms.filter((vm) => vm.fuelRobDataType === FuelRobDataTypeEnum.EstimationAtNoon).forEach((vm) =>
                    vm.fuelRobHfoPerTankViewModels?.forEach((f) => {
                        if (f.tankId === fuelRobPerTank.tankId) {
                            f.previousRobInMt = fuelRobPerTank.robInMt;
                            f.previousRobInM3 = fuelRobPerTank.robInM3;
                        }
                    })
                );
            }
            if (fuelRobPerTank.fuelType === FuelTypeEnum.MGO) {
                vms.filter((vm) => vm.fuelRobDataType === FuelRobDataTypeEnum.EstimationAtNoon).forEach((vm) =>
                    vm.fuelRobMgoPerTankViewModels?.forEach((f) => {
                        if (f.tankId === fuelRobPerTank.tankId) {
                            f.previousRobInMt = fuelRobPerTank.robInMt;
                            f.previousRobInM3 = fuelRobPerTank.robInM3;
                        }
                    })
                );
            }
            setFuelRobVMs([...vms]);
        }
    };

    const initVM = () => {
        if (!selectedAolDataItem) return;

        setHasSyncError(false);

        const noonReport = selectedAolDataItem.noonReport;
        const aolItem = selectedAolDataItem.aolItem;

        if (!(noonReport?.fuelRobSection?.fuelRobData ?? aolItem?.fuelRobDatas)) return;
        const fuelRobDatas = getSyncedFuelRobData(aolItem?.fuelRobDatas, noonReport?.fuelRobSection?.fuelRobData);
        setSyncedFuelRobDatas(fuelRobDatas);

        const previousItemWithRobData = getPreviousItemWithRobData();
        const fuelRobVMs = fuelRobDatas
            .sort((a, b) => (isDateBefore(a.eventDateTime, b.eventDateTime) ? -1 : 1))
            .map((data, i) => {
                const fuelRobPerTankViewModels = getFuelRobPerTankViewModels(previousItemWithRobData, data.fuelRobPerTanks);

                const fuelRobVm: FuelRobVM = {
                    isFirstRobSectionInAol: i === 0,
                    portName: '',
                    dateUtc: data.dateUtc,
                    eventDateTime: data.eventDateTime,
                    fuelRobHfoPerTankViewModels: fuelRobPerTankViewModels.filter((f) => f.fuelType === FuelTypeEnum.HFO),
                    fuelRobMgoPerTankViewModels: fuelRobPerTankViewModels.filter((f) => f.fuelType === FuelTypeEnum.MGO),
                    fuelRobDataType: data.type,
                    indexInDay: data.indexInDay,
                };

                return fuelRobVm;
            });
        setFuelRobVMs([...fuelRobVMs]);
        setVM({
            sectionData: noonReport?.fuelRobSection?.sectionData ?? createSectionData(NoonReportSectionEnum.FuelRob),
            notValidFieldsArray: [],
            hasInvalidFields: false,
            isInitialized: true,
        });
    };

    const getSyncedFuelRobData = (aolDataItemFuelRobData: FuelRobData[] | undefined, noonReportFuelRobData: FuelRobData[] | undefined) => {
        if (isNewAol(selectedAolDataItem) || (!noonReportFuelRobData && !aolDataItemFuelRobData)) {
            // New AOL detected - creating FuelRobData list from aolDataItem
            return aolDataItemFuelRobData;
        } else if (!noonReportFuelRobData && aolDataItemFuelRobData) {
            // No FuelRobData in NoonReport - returning FuelRobData list from aolDataItem
            return aolDataItemFuelRobData;
        } else if (noonReportFuelRobData && !aolDataItemFuelRobData) {
            return [];
        }

        //FuelRobData in AolItem and in NoonReport - syncing
        return getSyncedFuelRobDataWithAolAndNoonReportData(aolDataItemFuelRobData, noonReportFuelRobData);
    };

    const getSyncedFuelRobDataWithAolAndNoonReportData = (aolFuelRobData: FuelRobData[], noonReportFuelRobData: FuelRobData[]): FuelRobData[] => {
        let syncedNoonReportFuelRobData: FuelRobData[] = [];

        if (hasBunkeringMissingInAol(aolFuelRobData, noonReportFuelRobData)) {
            // The noon report has bunkering events which don't exist in the aol data. Likely caused by a deleted "bunkering complete" event
            setHasRemovedBunkeringSyncError(true);
            setHasSyncError(true);

            // find the bunkering events which exist in both noon report and aol data, discarding the deleted event.
            // This keeps the data in the noonreport for any remaining bunkering events already logged (in case of multiple bunkering in one day)
            let bunkeringInBothSources = noonReportFuelRobData.filter(
                (x) =>
                    x.type === FuelRobDataTypeEnum.MeasureAfterBunkering &&
                    aolFuelRobData.findIndex((y) => isDateSame(y.eventDateTime, x.eventDateTime)) >= 0
            );

            // Special edge case where a bunkering event has been duplicated
            if (bunkeringInBothSources && bunkeringInBothSources.length > aolFuelRobData.length) {
                bunkeringInBothSources = bunkeringInBothSources.filter((b, index) => {
                    const otherElements = [...bunkeringInBothSources];
                    otherElements.splice(index);
                    return otherElements.findIndex((e) => isDateSame(e.dateUtc, b.dateUtc)) !== -1;
                });
            }

            const estimationAtNoon = noonReportFuelRobData.filter((af) => af.type === FuelRobDataTypeEnum.EstimationAtNoon);
            if (estimationAtNoon?.[0]) syncedNoonReportFuelRobData.push(estimationAtNoon[0]);
            syncedNoonReportFuelRobData = syncedNoonReportFuelRobData.concat(bunkeringInBothSources);

            return syncedNoonReportFuelRobData;
        } else if (hasBunkeringMissingInNoonReport(aolFuelRobData, noonReportFuelRobData)) {
            // Bunkering events have been added since the noon report was created / saved
            setHasNewBunkeringSyncError(true);
            setHasSyncError(true);

            // Retain existing bunkering events in noon report if there already were some bunkering events logged before more were added
            const bunkeringInBothSources = noonReportFuelRobData.filter(
                (x) =>
                    x.type === FuelRobDataTypeEnum.MeasureAfterBunkering &&
                    aolFuelRobData.findIndex((y) => isDateSame(y.eventDateTime, x.eventDateTime)) >= 0
            );

            // Find the new bunkering events (not already in the noon report)
            const newInAol = aolFuelRobData.filter(
                (x) =>
                    x.type === FuelRobDataTypeEnum.MeasureAfterBunkering &&
                    noonReportFuelRobData.findIndex((y) => isDateSame(y.eventDateTime, x.eventDateTime)) === -1
            );

            const estimationAtNoon = noonReportFuelRobData.filter((af) => af.type === FuelRobDataTypeEnum.EstimationAtNoon);
            syncedNoonReportFuelRobData = syncedNoonReportFuelRobData.concat(bunkeringInBothSources, newInAol);
            if (estimationAtNoon?.[0]) syncedNoonReportFuelRobData.push(estimationAtNoon[0]);

            return syncedNoonReportFuelRobData;
        } else if (!hasSameDateTimes(aolFuelRobData, noonReportFuelRobData)) {
            setHasChangedBunkeringTimeSyncError(true);
            setChangedBunkeringTimeSyncErrorMessage(
                prettifyDateTimeHHmm(aolFuelRobData[0].eventDateTime) + ' != ' + prettifyDateTimeHHmm(noonReportFuelRobData[0].eventDateTime)
            );
            setHasSyncError(true);

            return syncDateTimes(aolFuelRobData, noonReportFuelRobData);
        }

        return noonReportFuelRobData;
    };

    const hasBunkeringMissingInNoonReport = (aolFuelRobData: FuelRobData[], noonReportFuelRobData: FuelRobData[]): boolean => {
        const aolMAB = aolFuelRobData.filter((af) => af.type === FuelRobDataTypeEnum.MeasureAfterBunkering);
        const noonReportMAB = noonReportFuelRobData.filter((af) => af.type === FuelRobDataTypeEnum.MeasureAfterBunkering);

        return aolMAB && noonReportMAB && aolMAB.length > noonReportMAB.length;
    };

    const hasBunkeringMissingInAol = (aolFuelRobData: FuelRobData[], noonReportFuelRobData: FuelRobData[]): boolean => {
        const aolMAB = aolFuelRobData.filter((af) => af.type === FuelRobDataTypeEnum.MeasureAfterBunkering);
        const noonReportMAB = noonReportFuelRobData.filter((af) => af.type === FuelRobDataTypeEnum.MeasureAfterBunkering);

        return noonReportMAB && aolMAB && noonReportMAB.length > aolMAB.length;
    };

    const hasSameDateTimes = (aolFuelRobData: FuelRobData[], noonReportFuelRobData: FuelRobData[]): boolean => {
        let hasSameDateTimes = true;

        const aolData = aolFuelRobData.filter((af) => af.type === FuelRobDataTypeEnum.MeasureAfterBunkering);
        const noonReportData = noonReportFuelRobData.filter((af) => af.type === FuelRobDataTypeEnum.MeasureAfterBunkering);

        // at this point, aolData and noonReportData should have the same number of bunkering events
        if (aolData.length !== noonReportData.length) return true;

        // for each bunkering in aol data, find out if at least 1 element of aolFuelRob can also be found in noonReportData, based on fuelType and eventDateTime
        // if unable to find 1 match, there is a datetime mismatch
        aolData.forEach((aolFuelRob) => {
            const foundFuelRobdata = noonReportData.findIndex((n) => {
                const foundFuelRobPerTank = aolFuelRob.fuelRobPerTanks.findIndex((f) => {
                    return (
                        isDateSame(n?.eventDateTime, aolFuelRob.eventDateTime) &&
                        f.fuelType === n.fuelRobPerTanks?.find((t) => t.tankId === f?.tankId)?.fuelType
                    );
                });

                if (foundFuelRobPerTank > -1) return 1;
                return -1;
            });
            if (foundFuelRobdata === -1) {
                hasSameDateTimes = false;
            }
            delete noonReportData[foundFuelRobdata];
        });

        return hasSameDateTimes;
    };

    const checkFuelTanksMatch = (fuelRobNoon: FuelRobData, fuelRobAol: FuelRobData, syncFuelRobList: FuelRobData[]): boolean => {
        let match = true;
        fuelRobNoon.fuelRobPerTanks.forEach((n_fr) => {
            const tankMatch = fuelRobAol.fuelRobPerTanks.find((af_fr) => {
                return af_fr.tankId === n_fr.tankId;
            });
            if (!tankMatch) {
                match = false;
                return;
            }
        });
        if (match) {
            const matchedFuelRob = new FuelRobData(
                fuelRobNoon.type,
                [...fuelRobNoon.fuelRobPerTanks],
                fuelRobNoon.portId,
                fuelRobNoon.dateUtc,
                fuelRobAol.indexInDay,
                fuelRobNoon.hfoCorrectionInMt,
                fuelRobNoon.mgoCorrectionInMt,
                fuelRobAol.eventDateTime
            );
            syncFuelRobList.push(matchedFuelRob as FuelRobData);
            return true;
        }
        return false;
    };

    const syncDateTimes = (aolFuelRobData: FuelRobData[], noonReportFuelRobData: FuelRobData[]): FuelRobData[] => {
        const aolAfterBunkering = aolFuelRobData.filter((af) => af.type === FuelRobDataTypeEnum.MeasureAfterBunkering);
        const noonReportFuelAfterBunkering = noonReportFuelRobData.filter((af) => af.type === FuelRobDataTypeEnum.MeasureAfterBunkering);

        const syncedRobData: FuelRobData[] = [];
        const fuelAtNoon = noonReportFuelRobData.find((n) => n.type === FuelRobDataTypeEnum.EstimationAtNoon);
        if (fuelAtNoon) syncedRobData.push(fuelAtNoon);

        // find bunkering with both time and tanks matching
        aolAfterBunkering.forEach((af) => {
            const index = noonReportFuelAfterBunkering.findIndex(
                (n) => isDateSame(n.eventDateTime, af.eventDateTime) && checkFuelTanksMatch(n, af, syncedRobData)
            );
            if (index > -1) noonReportFuelAfterBunkering.splice(index, 1);
        });

        // match remaining bunkerings based on tanks only
        aolAfterBunkering.forEach((af) => {
            const index = noonReportFuelAfterBunkering.findIndex((n) => checkFuelTanksMatch(n, af, syncedRobData));
            if (index > -1) noonReportFuelAfterBunkering.splice(index, 1);
        });

        return syncedRobData;
    };

    const isNewAol = (aolDataItem: AolDataItem | undefined): boolean => {
        return isNullOrUndefined(aolDataItem?.noonReport?.fuelRobSection);
    };

    const getTank = (tankId: number) => {
        return vessel?.nonCargoTanks.find((t) => t.tankId === tankId);
    };

    const getFuelRobPerTankViewModels = (
        previousItemWithRobData: AolDataItem | undefined,
        fuelRobPerTanks: FuelRobPerTank[],
        hasSyncError?: boolean
    ) => {
        const previousFuelRobData = getPreviousFuelRobData(previousItemWithRobData);

        return fuelRobPerTanks.map((fuelRobPerTank) => {
            const tank = getTank(fuelRobPerTank.tankId);
            let fuelType = fuelRobPerTank.fuelType;
            let sulphurPercent = fuelRobPerTank.sulphurPercent;
            let previousRob = fuelRobPerTank.previousRobInMt;
            let litreWeight = fuelRobPerTank.litreWeight;
            let robInMt = fuelRobPerTank.robInMt;
            const bunkering = fuelRobPerTank.bunkeringInMt;

            if (isNewAol(selectedAolDataItem) || hasSyncError) {
                const previousFuelRobPerTank = getPreviousFuelRobPerTank(previousFuelRobData, fuelRobPerTank.tankId);
                fuelType = previousFuelRobPerTank?.fuelType ? previousFuelRobPerTank.fuelType : GetFuelType(tank);
                previousRob = previousFuelRobPerTank?.robInMt ? previousFuelRobPerTank?.robInMt : 0;
                robInMt = previousRob;
                litreWeight = previousFuelRobPerTank?.litreWeight ? previousFuelRobPerTank.litreWeight : 0.95;
                sulphurPercent = previousFuelRobPerTank?.sulphurPercent ? previousFuelRobPerTank.sulphurPercent : 0;
            }

            const fuelRobPerTankVM: FuelRobPerTank = {
                tankId: fuelRobPerTank.tankId,
                tankName: tank?.odfjellTankName ?? tank?.tankName ?? (fuelType === FuelTypeEnum.HFO ? 'Other HFO tanks' : 'Other MGO tanks'),
                tankVolume: tank?.volume ? tank.volume : 10000,
                fuelType,
                robInMt,
                robInM3: truncateTo2Decimals(litreWeight !== undefined && litreWeight > 0 ? robInMt / litreWeight : 0),
                previousRobInMt: previousRob,
                previousRobInM3: truncateTo2Decimals(litreWeight !== undefined && litreWeight > 0 ? previousRob / litreWeight : 0),
                robDifferenceInMt: previousRob - robInMt,
                bunkeringInMt: bunkering,
                bunkeringInM3: truncateTo2Decimals(
                    bunkering !== undefined && litreWeight !== undefined && litreWeight > 0 ? bunkering / litreWeight : 0
                ),
                litreWeight,
                sulphurPercent,
            };

            return fuelRobPerTankVM;
        });
    };

    const getPreviousFuelRobData = (previousItemWithRobData: AolDataItem | undefined): FuelRobPerTank[] | undefined => {
        const data = isNewAol(previousItemWithRobData)
            ? previousItemWithRobData?.aolItem?.fuelRobDatas
            : previousItemWithRobData?.noonReport?.fuelRobSection?.fuelRobData;
        if (!data) return undefined;

        return data?.flatMap((d) => d.fuelRobPerTanks);
    };

    const getPreviousFuelRobPerTank = (fuelRobData: FuelRobPerTank[] | undefined, tankId: number) => {
        return fuelRobData?.find((t) => t.tankId === tankId);
    };

    const getPreviousItemWithRobData = (): AolDataItem | undefined => {
        if (!selectedAolGroup || !selectedAolDataItem) return undefined;

        const itemsWithRobData = selectedAolGroup.aolDataItems.filter((o) => hasBunkering(o));
        let previousItemWithRobData;

        const selectedItemWithRobDataIndex = itemsWithRobData.findIndex(
            (a) =>
                a.reportFromTimeLocalTime === selectedAolDataItem.reportFromTimeLocalTime &&
                a.reportToTimeLocalTime === selectedAolDataItem.reportToTimeLocalTime
        );
        if (selectedItemWithRobDataIndex > 0) {
            previousItemWithRobData = itemsWithRobData[selectedItemWithRobDataIndex - 1];
        } else {
            const previousItem = itemsWithRobData
                .filter(
                    (i) =>
                        i.reportFromTimeLocalTime &&
                        i.reportFromTimeLocalTime &&
                        selectedAolDataItem.reportFromTimeLocalTime &&
                        i.reportFromTimeLocalTime < selectedAolDataItem.reportFromTimeLocalTime
                )
                .pop();
            if (previousItem) {
                previousItemWithRobData = previousItem;
            } else if (previousAolGroup?.aolDataItems !== undefined && previousAolGroup.aolDataItems.length > 0) {
                previousItemWithRobData = previousAolGroup.aolDataItems[previousAolGroup.aolDataItems.length - 1];
            }
        }

        return previousItemWithRobData;
    };

    const getFuelRobDataFromVM = () => {
        if (!fuelRobVMs) return [];
        return fuelRobVMs.map((vm) => {
            const fuelRobPerTanks = vm.fuelRobHfoPerTankViewModels?.concat(vm.fuelRobMgoPerTankViewModels);
            return new FuelRobData(vm.fuelRobDataType, fuelRobPerTanks, 0, vm.dateUtc, vm.indexInDay, undefined, undefined, vm.eventDateTime);
        });
    };

    const save = () => {
        vm.fuelRobData = getFuelRobDataFromVM();
        const data = new NoonReportFuelRobSectionFactory().createBM(vm);
        if (!data.sectionData || data.sectionData.section === 0)
            data.sectionData = {
                noonReportId: 0,
                section: NoonReportSectionEnum.FuelRob,
                isApproved: false,
                isCompleted: false,
                role: 1,
            };

        if (!vm.hasInvalidFields) {
            saveSection(data);
        }
    };

    const onAcceptSyncClick = () => {
        if (!selectedAolDataItem?.noonReport) return;
        vm.fuelRobData = getFuelRobDataFromVM();
        const data = new NoonReportFuelRobSectionFactory().createBM(vm);
        syncFuelRobMismatch(selectedAolDataItem.noonReport, data);
        selectedAolDataItem.noonReport.fuelRobSection.fuelRobData = syncedFuelRobDatas;
        setHasSyncError(false);
        setHasNewBunkeringSyncError(false);
        setHasRemovedBunkeringSyncError(false);
        setHasChangedBunkeringTimeSyncError(false);
    };

    return (
        fuelRobVMs && (
            <>
                {!hasSyncError &&
                    fuelRobVMs.map(
                        (fuelRobVM, i) =>
                            fuelRobVM.fuelRobHfoPerTankViewModels &&
                            fuelRobVM.fuelRobMgoPerTankViewModels && (
                                <Wrapper key={`${fuelRobVM.fuelRobDataType}_${i}`}>
                                    {fuelRobVM.fuelRobDataType === FuelRobDataTypeEnum.MeasureAfterBunkering && (
                                        <>
                                            {fuelRobVM.fuelRobHfoPerTankViewModels.length > 0 && (
                                                <FuelRobBunkerComponent
                                                    vm={vm}
                                                    isReadOnly={isReadOnly}
                                                    sectionData={vm.sectionData}
                                                    fuelRobPerTanks={fuelRobVM.fuelRobHfoPerTankViewModels}
                                                    fuelType={FuelTypeEnum.HFO}
                                                    hasEmptyValues={hasEmptyHfoValues}
                                                    portName={fuelRobVM.portName}
                                                    fuelRobDataType={fuelRobVM.fuelRobDataType}
                                                    unit={selectedUnit}
                                                    updatePreviousROB={updatePreviousROB}
                                                    fuelRobVM={fuelRobVM}
                                                    aolDataItem={selectedAolDataItem}
                                                />
                                            )}
                                            {fuelRobVM.fuelRobMgoPerTankViewModels.length > 0 && (
                                                <FuelRobBunkerComponent
                                                    vm={vm}
                                                    isReadOnly={isReadOnly}
                                                    sectionData={vm.sectionData}
                                                    fuelRobPerTanks={fuelRobVM.fuelRobMgoPerTankViewModels}
                                                    fuelType={FuelTypeEnum.MGO}
                                                    hasEmptyValues={hasEmptyMgoValues}
                                                    portName={fuelRobVM.portName}
                                                    fuelRobDataType={fuelRobVM.fuelRobDataType}
                                                    unit={selectedUnit}
                                                    updatePreviousROB={updatePreviousROB}
                                                    fuelRobVM={fuelRobVM}
                                                    aolDataItem={selectedAolDataItem}
                                                />
                                            )}
                                        </>
                                    )}
                                    {fuelRobVM.fuelRobDataType === FuelRobDataTypeEnum.EstimationAtNoon && (
                                        <>
                                            {fuelRobVM.fuelRobHfoPerTankViewModels.length > 0 && (
                                                <FuelRobPerTypeComponent
                                                    vm={vm}
                                                    isReadOnly={isReadOnly}
                                                    sectionData={vm.sectionData}
                                                    fuelRobPerTanks={fuelRobVM.fuelRobHfoPerTankViewModels}
                                                    fuelType={FuelTypeEnum.HFO}
                                                    hasEmptyValues={hasEmptyHfoValues}
                                                    portName={fuelRobVM.portName}
                                                    fuelRobDataType={fuelRobVM.fuelRobDataType}
                                                    fuelRobVM={fuelRobVM}
                                                    unit={selectedUnit}
                                                />
                                            )}
                                            {fuelRobVM.fuelRobMgoPerTankViewModels.length > 0 && (
                                                <FuelRobPerTypeComponent
                                                    vm={vm}
                                                    isReadOnly={isReadOnly}
                                                    sectionData={vm.sectionData}
                                                    fuelRobPerTanks={fuelRobVM.fuelRobMgoPerTankViewModels}
                                                    fuelType={FuelTypeEnum.MGO}
                                                    hasEmptyValues={hasEmptyMgoValues}
                                                    portName={fuelRobVM.portName}
                                                    fuelRobDataType={fuelRobVM.fuelRobDataType}
                                                    fuelRobVM={fuelRobVM}
                                                    unit={selectedUnit}
                                                />
                                            )}
                                        </>
                                    )}
                                </Wrapper>
                            )
                    )}
                {hasSyncError && (
                    <ErrorText className="error-text">
                        This AOL Fuel ROB section does not match reported bunkering events.
                        <br />
                        Please click on &apos;Accept&apos; to synchronize this AOL with the reported events.
                        <br />
                        {hasNewBunkeringSyncError && <span>New bunkering operation detected</span>}
                        {hasRemovedBunkeringSyncError && <span>Bunkering operation removed from this AOL detected</span>}
                        {hasChangedBunkeringTimeSyncError && (
                            <span>
                                Bunkering complete date/time change detected:
                                <br />
                                {changedBunkeringTimeSyncErrorMessage}
                            </span>
                        )}
                        <br />
                        <button onClick={onAcceptSyncClick}>Accept</button>
                    </ErrorText>
                )}
            </>
        )
    );
};
export default FuelRobSectionComponent;
